// Display formats (SPEC §4 money, §10 exports, §11 freshness). English separators everywhere.

const usdFull = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export function formatUsdFull(n: number): string {
  return usdFull.format(n);
}

export function formatUsdCompact(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  const [div, suffix] = abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : abs >= 1e3 ? [1e3, "K"] : [1, ""];
  const v = abs / div;
  const s = div === 1 ? Math.round(v).toString() : (Math.round(v * 10) / 10).toString();
  return `${sign}$${s}${suffix}`;
}

/** A file size for an attachment list: bytes up to 1 KB, then KB, then MB with one decimal. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)} MB`;
}

/** Order-currency amount for the PO drawer. KIDR (thousand IDR) is shown as IDR. */
export function formatOrderAmount(n: number, currency: string): string {
  let code = currency.toUpperCase();
  let value = n;
  if (code === "KIDR") {
    code = "IDR";
    value = n * 1000;
  }
  let digits = 2;
  try {
    digits = new Intl.NumberFormat("en-US", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    // Not an ISO code Intl knows; keep 2 decimals.
  }
  // Intl gives IDR 2 decimals, but rupiah amounts are shown whole.
  if (code === "IDR") digits = 0;
  const num = new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
  return `${code} ${num}`;
}

export const WIB_ZONE = "Asia/Jakarta";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** WIB is UTC+7 with no daylight saving, so the parts can be computed without Intl zones. */
function wibParts(d: Date) {
  const w = new Date(d.getTime() + 7 * 3600_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    y: w.getUTCFullYear(),
    mo: w.getUTCMonth(),
    mm: pad(w.getUTCMonth() + 1),
    d: w.getUTCDate(),
    dd: pad(w.getUTCDate()),
    hh: pad(w.getUTCHours()),
    mi: pad(w.getUTCMinutes()),
  };
}

export function formatWib(d: Date): string {
  const p = wibParts(d);
  return `${p.hh}:${p.mi} WIB`;
}

export function formatWibLong(d: Date): string {
  const p = wibParts(d);
  return `${p.hh}:${p.mi} WIB, ${p.d} ${MONTHS[p.mo]}`;
}

/** "Data as of 14:05 WIB, 22 Oct 2026"; the budget export also carries "Budgets as of" (SPEC §10). */
export function exportStamp(asOf: Date, delayed: boolean, what: "Data" | "Budgets" = "Data"): string {
  const p = wibParts(asOf);
  return `${what} as of ${p.hh}:${p.mi} WIB, ${p.d} ${MONTHS[p.mo]} ${p.y}${delayed ? " (sync delayed)" : ""}`;
}

export function exportFilename(table: string, at: Date): string {
  const p = wibParts(at);
  return `${table}_${p.y}-${p.mm}-${p.dd}_${p.hh}${p.mi}WIB.xlsx`;
}

/** Sertica dates are server-local with no offset; show them as calendar dates. */
export function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  // A string without a zone is Sertica wall-clock time: read it as UTC, never as browser-local.
  const x = typeof d === "string" ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(d) ? d : `${d.replace(" ", "T")}Z`) : d;
  return `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]} ${x.getUTCFullYear()}`;
}

/** A Sertica timestamp with its wall-clock time: "22 Jun 2026 12:37". */
export function formatDateTime(d: string | null | undefined): string {
  if (!d) return "—";
  const t = d.replace(" ", "T").slice(11, 16);
  return t ? `${formatDate(d)} ${t}` : formatDate(d);
}
